import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import { CHAR_LIMIT } from "../../../../../utils/Common/Constant";
import { useEffect, useRef } from "react";
import { ProgressReportPrefill } from "hooks/useConstructionProgressReport";
import Card from "../Card";
import FormField from "../FormField";
import SectionHeader from "../SectionHeader";

interface Props {
  prefill?: ProgressReportPrefill;
  drawAssessmentText?: string;
  onDrawAssessmentTextChange?: (val: string) => void;
  drawReviewBullets?: string;
  onDrawReviewBulletsChange?: (val: string) => void;
  changeOrders?: string;
  onChangeOrdersChange?: (val: string) => void;
  offsiteMaterialsNotes?: string;
  onOffsiteMaterialsNotesChange?: (val: string) => void;
  signatureDisclaimer?: string;
  onSignatureDisclaimerChange?: (val: string) => void;
  bankItems?: string;
  onBankItemsChange?: (val: string) => void;
  submitted?: boolean;
}

const Step2DrawReview = ({
  prefill,
  drawAssessmentText = "",
  onDrawAssessmentTextChange,
  drawReviewBullets = "",
  onDrawReviewBulletsChange,
  changeOrders = "",
  onChangeOrdersChange,
  offsiteMaterialsNotes = "",
  onOffsiteMaterialsNotesChange,
  signatureDisclaimer = "",
  onSignatureDisclaimerChange,
  bankItems = "",
  onBankItemsChange,
  submitted = false,
}: Props) => {
  const notes = prefill?.inspection?.note;
  const rootRef = useRef<HTMLDivElement>(null);

  const assessmentErr =
    submitted && !drawAssessmentText.trim()
      ? "Overall Draw Assessment is required"
      : "";
  const bulletErr =
    submitted && !drawReviewBullets.replace(/^•\s*/gm, "").trim()
      ? "Key Draw Review Bullet Points is required"
      : "";
  const bankErr =
    submitted && !bankItems.trim()
      ? "Items for Bank to Verify is required"
      : "";

  useEffect(() => {
    if (notes?.bank && !bankItems) onBankItemsChange?.(notes.bank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes?.bank]);

  useEffect(() => {
    if (submitted && (assessmentErr || bulletErr || bankErr)) {
      rootRef.current
        ?.querySelector<HTMLElement>(".field-error")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted]);

  const handleBulletFocus = () => {
    if (!drawReviewBullets) onDrawReviewBulletsChange?.("• ");
  };

  const handleBulletKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next =
      drawReviewBullets.substring(0, start) +
      "\n• " +
      drawReviewBullets.substring(end);
    onDrawReviewBulletsChange?.(next);
    setTimeout(() => {
      el.selectionStart = el.selectionEnd = start + 3;
    }, 0);
  };

  return (
    <div className="craScreen" ref={rootRef}>
      <SectionHeader
        title="Draw Review"
        subtitle="Review and edit draw review narrative. Notes from field inspection are pre-populated."
      />

      <Card
        title="Draw Review Summary Notes"
        badge={{ label: "Editable", variant: "editable" }}
      >
        <div className="craFieldRow">
          <FormField
            label="Overall Draw Assessment"
            required
            as="textarea"
            rows={6}
            value={drawAssessmentText}
            onChange={onDrawAssessmentTextChange}
            error={assessmentErr}
          />
        </div>
        <div className="craFieldRow">
          <div className="craField">
            <label>
              Key Draw Review Bullet Points <span className="req">*</span>
            </label>
            <TextArea
              rows={10}
              value={drawReviewBullets}
              onChange={(e) => onDrawReviewBulletsChange?.(e.target.value)}
              onFocus={handleBulletFocus}
              onKeyDown={handleBulletKeyDown}
              className={bulletErr ? "error-input-red" : ""}
              maxChars={CHAR_LIMIT.DESCRIPTION}
            />
            {bulletErr && <p className="field-error">{bulletErr}</p>}
          </div>
        </div>
        <div className="craFieldRow cols2">
          <FormField
            label="Change Orders"
            value={changeOrders || "There are no change orders to date"}
            onChange={onChangeOrdersChange}
          />
          <FormField
            label="Off-Site Stored Materials"
            value={
              offsiteMaterialsNotes ||
              "No billing for off-site stored materials this period"
            }
            onChange={onOffsiteMaterialsNotesChange}
          />
        </div>
      </Card>

      <Card
        title="Bank / Lender Action Items"
        badge={{ label: "Editable", variant: "editable" }}
      >
        <div className="craFieldRow">
          <FormField
            label="Items for Bank to Verify"
            required
            as="textarea"
            value={bankItems}
            onChange={onBankItemsChange}
            error={bankErr}
          />
        </div>
        <div className="craFieldRow">
          <FormField
            label="Signature / Disclaimer Note"
            as="textarea"
            value={signatureDisclaimer}
            onChange={onSignatureDisclaimerChange}
          />
        </div>
      </Card>
    </div>
  );
};

export default Step2DrawReview;
